import React from "react";
import DietPlanTab from "../../../features/diet/workflow/plan/view/DietPlanTab";
import ActivityTab from "../../../features/workouts/workflow/profile/view/ActivityTab";
import NotesTab from "../../../features/notes/workflow/profile/view/NotesTab";
import HormonalWellnessTab from "../../../features/hormones/workflow/HormonalWellnessTab";
import GutHealthTab from "../../../features/gut/workflow/GutHealthTab";
import UserAnalytics from "./userAnalytics/UserAnalytics";
import Subscriptions from "./subscriptions/Subscriptions";
import { serializeUserNotesKey } from "../../../utils/listCache/caches";

/**
 * @typedef {{ profileId: string, showAlert?: (message: string) => void, onSubscriptionsChanged?: () => void }} TabContext
 * @typedef {{ id: string, label: string, permission?: string, appOnly?: boolean, coaching?: boolean,
 *   render: (ctx: TabContext) => React.ReactNode }} ProfileTab
 */

/** Button-permission page the profile tab permissions live under (configs/accessControl.js). */
export const PROFILE_PERMISSION_PAGE = "user-dashboard";

/**
 * The client profile tabs, in display order. `appOnly` tabs need a real app
 * account (trackers, plans, notes); prospects only get the others.
 * `coaching` tabs (trackers and coach notes) are for coaching a client; the Lead page leaves them out.
 * @type {ProfileTab[]}
 */
export const PROFILE_TABS = [
  {
    id: "Nutrition",
    label: "Nutrition",
    coaching: true,
    appOnly: true,
    render: ({ profileId, showAlert }) => (
      <DietPlanTab profileId={profileId} showAlert={showAlert} />
    ),
  },
  {
    id: "Activity",
    label: "Activity",
    coaching: true,
    appOnly: true,
    render: ({ profileId, showAlert }) => (
      <ActivityTab profileId={profileId} showAlert={showAlert} />
    ),
  },
  {
    id: "User Analytics",
    label: "User Analytics",
    coaching: true,
    appOnly: true,
    render: ({ profileId, showAlert }) => (
      <UserAnalytics profileId={profileId} showAlert={showAlert} />
    ),
  },
  {
    id: "Notes",
    label: "Notes",
    coaching: true,
    appOnly: true,
    render: ({ profileId }) => (
      <NotesTab
        key={profileId}
        profileId={profileId}
        cacheKey={serializeUserNotesKey(profileId)}
      />
    ),
  },
  {
    id: "Hormonal Wellness",
    label: "Hormonal Wellness",
    coaching: true,
    permission: "view-hormonal-wellness-tab",
    appOnly: true,
    render: ({ profileId }) => (
      <HormonalWellnessTab key={profileId} profileId={profileId} />
    ),
  },
  {
    id: "Gut Health",
    label: "Gut Health",
    coaching: true,
    permission: "view-gut-health-tab",
    appOnly: true,
    render: ({ profileId }) => (
      <GutHealthTab key={profileId} profileId={profileId} />
    ),
  },
  {
    id: "Subscriptions",
    label: "Subscriptions",
    permission: "view-subscriptions-tab",
    appOnly: false,
    render: ({ profileId, showAlert, onSubscriptionsChanged }) => (
      <Subscriptions
        profileId={profileId}
        showAlert={showAlert}
        onChanged={onSubscriptionsChanged}
      />
    ),
  },
];
